import {
    ColDef,
    ColGroupDef,
    ICellRendererParams,
    ValueFormatterParams,
} from 'ag-grid-community';
import { FULL_MONTHS } from 'helpers/dateHelper';
import { t } from 'i18next';
import {
    DateString,
    getUniqueYearsFromDateStrings,
} from 'routes/HumanResourcesModule/business/types';
import { getPivotedField } from 'routes/HumanResourcesModule/helpers/dataHelpers';
import {
    getMonthList,
    isRowPinned,
} from 'routes/HumanResourcesModule/Simulation/helpers/gridHelpers';
import { SimulationDateString } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import {
    HISTORIC_AMOUNT,
    SIMULATED_AMOUNT,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import {
    PreviousSimulationTypeOfLine,
    PREVIOUS_SIMULATION_TYPE_OF_LINE,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/ValueByDate/helpers/dataHelpers';
import { currencyFormatter } from 'ui/AgGridReact/formatters';

export const getHistoricColDefs = (
    simulationStart: SimulationDateString,
): ColGroupDef[] => {
    const previousYear = new Date(simulationStart).getFullYear() - 1;
    const previousYearStart: SimulationDateString = `${previousYear}-01-01`;
    const monthsList = getMonthList(previousYearStart, simulationStart, false);
    return getUniqueYearsFromDateStrings(monthsList).map((year) => ({
        groupId: year.toString(),
        headerName: year.toString(),
        children: monthsList
            .filter((d) => parseInt(d.slice(0, 4), 10) === year)
            .map((d) => ({
                field: getPivotedField(HISTORIC_AMOUNT, d),
                valueGetter: ({ data }) =>
                    data[getPivotedField(HISTORIC_AMOUNT, d)] ?? 0,
                valueFormatter: currencyFormatter,
                headerName: FULL_MONTHS[parseInt(d.slice(4, 6), 10) - 1],
            })),
    }));
};

const previousSimulationCellRendererSelector = (
    params: ICellRendererParams,
) => {
    if (
        params.data[PREVIOUS_SIMULATION_TYPE_OF_LINE] ===
            PreviousSimulationTypeOfLine.REAL_DATA ||
        isRowPinned(params)
    ) {
        return { component: undefined };
    }
    return {
        component: 'editFieldCellRenderer',
    };
};

export const getPreviousSimulationColDefs = ({
    simulationStart,
    simulationEnd,
    setValueOfDate,
    cellColorSelector,
    isUpdateEnabled,
    translationKey,
}: {
    simulationStart: SimulationDateString;
    simulationEnd: SimulationDateString;
    setValueOfDate: (d: DateString, v: number) => void;
    cellColorSelector: (d: DateString) => string;
    isUpdateEnabled: boolean;
    translationKey: string;
}): (ColGroupDef | ColDef)[] => {
    const monthsList = getMonthList(simulationStart, simulationEnd, false);

    const colDefs: (ColGroupDef | ColDef)[] = getUniqueYearsFromDateStrings(
        monthsList,
    ).map((year) => ({
        groupId: year.toString(),
        headerName: year.toString(),
        children: monthsList
            .filter((d) => parseInt(d.slice(0, 4), 10) === year)
            .map((d) => ({
                field: getPivotedField(SIMULATED_AMOUNT, d),
                valueGetter: ({ data }) =>
                    data[getPivotedField(SIMULATED_AMOUNT, d)] ?? 0,
                valueFormatter: (
                    params: Partial<ValueFormatterParams>,
                    forceSign?: boolean,
                ) => currencyFormatter(params, forceSign, false),
                headerName: FULL_MONTHS[parseInt(d.slice(4, 6), 10) - 1],
                cellRendererSelector: previousSimulationCellRendererSelector,
                cellRendererParams: {
                    onChange: (value: string) => {
                        setValueOfDate(d, parseFloat(value));
                    },
                    sx: { color: cellColorSelector(d) },
                    isUpdateEnabled,
                },
                suppressKeyboardEvent: () => true, // so we can use keys to navigate within text field
            })),
    }));

    colDefs.push({
        field: PREVIOUS_SIMULATION_TYPE_OF_LINE,
        pinned: 'left',
        headerName: t('common.month'),
        width: 150,
        valueGetter: ({ data }) => data[PREVIOUS_SIMULATION_TYPE_OF_LINE],
        valueFormatter: ({ value }) => t(`${translationKey}.${value}`),
    });
    return colDefs;
};
